import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAITaskRegistration } from "../lib/aiTasks";
import { ChatCircleText, Sparkle, ArrowsClockwise, Info, Buildings, Heart, UsersThree, Coins, ChartLineUp, CaretLeft } from "@phosphor-icons/react";

const SECTIONS = [
  {
    id: "company", title: "Wat doet het bedrijf?", icon: Buildings,
    hint: "Geef de website van het bedrijf en beschrijf in eigen woorden wat ze doen. De AI vergelijkt jouw beschrijving met de website.",
    fieldKey: "company_description", feedbackKey: "company_ai_feedback", checkPath: "company", extraField: "company_url",
  },
  {
    id: "why-company", title: "Waarom vind je dit een leuk bedrijf?", icon: Heart,
    hint: "Vertel waarom je dit zo'n leuk bedrijf vindt om voor te werken. Geef oprechte complimenten over wat ze doen — vermijd zelfpromotie.",
    fieldKey: "why_company_attractive", feedbackKey: "why_company_attractive_ai_feedback", checkPath: "why-company",
  },
  {
    id: "why-people", title: "Waarom werken er leuke mensen?", icon: UsersThree,
    hint: "Beschrijf waarom je denkt dat er leuke mensen werken en waarom je er goed bij past. Houd de focus op hen, niet op jezelf.",
    fieldKey: "why_people_attractive", feedbackKey: "why_people_attractive_ai_feedback", checkPath: "why-people",
  },
  {
    id: "value", title: "Hoe voeg je waarde toe?", icon: Coins,
    hint: "Leg kort uit hoe je waarde toevoegt aan deze organisatie. De AI checkt dit tegen je CV.",
    fieldKey: "value_proposition", feedbackKey: "value_proposition_ai_feedback", checkPath: "value", needsCv: true,
  },
];

const MD_COMPONENTS = {
  h1: ({ node, ...p }) => <h2 className="font-display text-lg font-bold mt-2 mb-2" {...p} />,
  h2: ({ node, ...p }) => <h3 className="font-display text-base font-bold mt-3 mb-1.5 text-zinc-800" {...p} />,
  h3: ({ node, ...p }) => <h4 className="font-display text-sm font-bold mt-2.5 mb-1 text-zinc-800 uppercase" {...p} />,
  p: ({ node, ...p }) => <p className="text-[14px] leading-relaxed text-zinc-700 my-1.5" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-5 my-1.5 space-y-0.5 text-[14px] text-zinc-700" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-5 my-1.5 space-y-0.5 text-[14px] text-zinc-700" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-semibold text-zinc-900" {...p} />,
  em: ({ node, ...p }) => <em className="italic" {...p} />,
};

function displayUrl(u) {
  if (!u || !u.trim()) return "(zonder URL)";
  return u.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

export default function CoacheeCoverLetterPage() {
  const { letterId } = useParams();
  const navigate = useNavigate();
  const [coacheeId, setCoacheeId] = useState(null);
  const [meta, setMeta] = useState(null);             // {organization, function, application_date, organization_url}
  const [letter, setLetter] = useState(null);
  const [hasCv, setHasCv] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [checking, setChecking] = useState({});
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const activeId = letterId;

  useAITaskRegistration({
    active: generating && !!coacheeId && !!activeId,
    id: coacheeId && activeId ? `cover-letter-${coacheeId}-${activeId}` : null,
    label: "Motivatiebrief schrijven met AI",
    returnTo: activeId ? `/sollicitaties/${activeId}` : "/sollicitaties",
  });

  // Bootstrap
  useEffect(() => {
    (async () => {
      try {
        const { data: u } = await api.get("/auth/me");
        const { data: list } = await api.get("/coachees");
        const own = list.find((p) => p.user_id === u.id) || null;
        if (!own) { setLoading(false); return; }
        setCoacheeId(own.id);
        if (!letterId) {
          // No letter id in the URL — go back to the list (this guards old
          // bookmarks to /cover-letter).
          navigate("/sollicitaties", { replace: true });
          return;
        }
        // Load the letter itself + the application metadata from the list endpoint.
        const [letterRes, listRes] = await Promise.all([
          api.get(`/coachees/${own.id}/cover-letter/${letterId}`),
          api.get(`/coachees/${own.id}/cover-letters`),
        ]);
        setLetter(letterRes.data.letter);
        setHasCv(letterRes.data.has_cv);
        const m = (listRes.data || []).find((x) => x.id === letterId) || null;
        setMeta(m);
        setSavedAt(null);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [letterId]);

  const update = (patch) => setLetter((l) => ({ ...l, ...patch }));

  // Debounced autosave
  useEffect(() => {
    if (!coacheeId || !activeId || !letter) return;
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        await api.put(`/coachees/${coacheeId}/cover-letter/${activeId}`, letter);
        setSavedAt(new Date());
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally { setSaving(false); }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [letter]);

  const runCheck = async (section) => {
    if (!coacheeId || !activeId) return;
    setChecking((s) => ({ ...s, [section.id]: true }));
    setError("");
    try {
      await api.put(`/coachees/${coacheeId}/cover-letter/${activeId}`, letter);
      const { data } = await api.post(
        `/coachees/${coacheeId}/cover-letter/${activeId}/check/${section.checkPath}`,
        null,
        { timeout: 90000 }
      );
      update({ [section.feedbackKey]: data.feedback });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setChecking((s) => ({ ...s, [section.id]: false }));
    }
  };

  const generate = async () => {
    if (!coacheeId || !activeId) return;
    setGenerating(true); setError("");
    try {
      await api.put(`/coachees/${coacheeId}/cover-letter/${activeId}`, letter);
      const { data } = await api.post(
        `/coachees/${coacheeId}/cover-letter/${activeId}/generate`,
        null,
        { timeout: 120000 }
      );
      update({ letter_text: data.letter_text });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setGenerating(false); }
  };

  if (loading) return <AppLayout title="Sollicitaties"><div className="text-zinc-500">Laden...</div></AppLayout>;
  if (!letter) return <AppLayout title="Sollicitaties"><div className="text-zinc-500">Geen profiel gevonden.</div></AppLayout>;

  const allFilled = SECTIONS.every((s) => (letter[s.fieldKey] || "").trim());

  return (
    <AppLayout
      title="Motivatiebrief"
      breadcrumbs={[
        { label: "Sollicitaties", to: "/sollicitaties" },
        { label: meta?.organization || displayUrl(letter?.company_url) || "Brief" },
      ]}
    >
      <div className="max-w-3xl space-y-8" data-testid="cover-letter-page">

        {/* Back link + application context */}
        <section className="neuro-card p-4 bg-white" data-testid="cl-context">
          <Link
            to="/sollicitaties"
            className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-900 transition-colors"
            data-testid="cl-back-link"
          >
            <CaretLeft size={12} /> Terug naar sollicitaties
          </Link>
          {meta && (meta.organization || meta.function || meta.application_date) && (
            <div className="mt-2 flex items-center gap-3 flex-wrap text-sm">
              {meta.organization && (
                <span className="font-display text-base font-bold tracking-tight" data-testid="cl-meta-org">
                  {meta.organization}
                </span>
              )}
              {meta.function && (
                <span className="text-zinc-700" data-testid="cl-meta-function">
                  · {meta.function}
                </span>
              )}
              {meta.application_date && (
                <span className="text-xs text-zinc-500" data-testid="cl-meta-date">
                  · {meta.application_date}
                </span>
              )}
            </div>
          )}
        </section>

        {/* Intro */}
        <section className="neuro-card-bold p-6 bg-white" data-testid="cl-intro">
          <div className="flex items-start gap-3">
            <div className="inline-flex h-10 w-10 items-center justify-center bg-[#1E88E5] text-white rounded-sm shrink-0">
              <ChatCircleText size={20} weight="fill" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-bold tracking-tight">Een brief die anders is dan anders</h2>
              <div className="text-[15px] leading-relaxed text-zinc-700 mt-2 space-y-2">
                <p>
                  De meeste sollicitatiebrieven gaan over de sollicitant zelf. Maar mensen reageren juist veel beter op
                  <strong> complimenten</strong> en willen <strong>vooral over zichzelf horen</strong>. Daarom draaien we het om:
                  jouw brief gaat <strong>vooral over het bedrijf</strong> en maar weinig over jou.
                </p>
                <p>
                  Deze aanpak werkt veel beter dan een brief vol zelfpromotie. Hij is <strong>perfect voor open sollicitaties</strong> —
                  en open sollicitaties hebben één groot voordeel: <strong>geen tegenkandidaten</strong>. Met deze methode is de
                  kans dat je wordt uitgenodigd voor een gesprek het grootst.
                </p>
                <p className="text-sm text-zinc-500">
                  Vul de vier onderdelen in. De AI helpt je per onderdeel met feedback. Daarna maakt de AI op
                  basis van jouw input een complete brief klaar.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Status */}
        <div className="flex items-center justify-between text-xs text-zinc-500">
          <span data-testid="cl-saved-status">
            {saving ? "Opslaan..." : savedAt ? `Automatisch opgeslagen om ${savedAt.toLocaleTimeString()}` : "Nog niet gewijzigd"}
          </span>
          {error && <span className="text-[#E53935]" data-testid="cl-error">{error}</span>}
        </div>

        {/* Sections 1-4 */}
        {SECTIONS.map((section, idx) => {
          const Icon = section.icon;
          const value = letter[section.fieldKey] || "";
          const feedback = letter[section.feedbackKey] || "";
          const isChecking = !!checking[section.id];
          const blocked = section.needsCv && !hasCv;
          return (
            <section key={section.id} className="neuro-card p-6 bg-white" data-testid={`cl-section-${section.id}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="inline-flex h-9 w-9 items-center justify-center bg-zinc-900 text-white rounded-sm shrink-0">
                  <span className="text-sm font-bold">{idx + 1}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Icon size={20} weight="duotone" className="text-[#1E88E5]" />
                    <h3 className="font-display text-lg font-bold tracking-tight">{section.title}</h3>
                  </div>
                  <p className="text-sm text-zinc-500 mt-1">{section.hint}</p>
                </div>
              </div>

              {section.extraField === "company_url" && (
                <div className="mt-3 mb-3">
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Website van het bedrijf</label>
                  <input
                    type="url"
                    value={letter.company_url || ""}
                    onChange={(e) => update({ company_url: e.target.value })}
                    placeholder="https://bedrijfsnaam.nl"
                    data-testid="cl-company-url"
                    className="w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2 text-[14px] rounded-sm bg-white"
                  />
                  <p className="text-[11px] text-zinc-400 mt-1">Tip: bij meerdere sollicitaties wordt elke brief opgeslagen onder zijn eigen URL. Wissel via de dropdown bovenaan.</p>
                </div>
              )}

              <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">
                {section.extraField === "company_url" ? "Wat doet dit bedrijf? (in je eigen woorden)" : "Jouw tekst"}
              </label>
              <textarea
                value={value}
                onChange={(e) => update({ [section.fieldKey]: e.target.value })}
                placeholder="Schrijf hier..."
                rows={5}
                data-testid={`cl-${section.id}-input`}
                className="w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2 text-[14.5px] rounded-sm bg-white leading-relaxed"
              />

              {blocked ? (
                <div className="mt-3 border-l-4 border-[#FB8C00] bg-[#FB8C00]/5 px-3 py-2 rounded-r-sm text-sm text-[#E65100] flex items-start gap-2" data-testid="cl-value-needs-cv">
                  <Info size={16} weight="fill" className="mt-0.5 shrink-0" />
                  <span>Maak eerst je CV op om te zorgen dat de AI dit kan controleren.</span>
                </div>
              ) : (
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => runCheck(section)}
                    disabled={isChecking || !value.trim()}
                    data-testid={`cl-${section.id}-check`}
                    className="inline-flex items-center gap-2 bg-[#1E88E5] text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1976D2] disabled:opacity-50 transition-colors"
                  >
                    {isChecking ? <ArrowsClockwise size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
                    {isChecking ? "AI denkt mee..." : feedback ? "Controleer opnieuw" : "Controleer met AI"}
                  </button>
                  <span className="text-xs text-zinc-500">Duurt ongeveer 5-15 seconden.</span>
                </div>
              )}

              {feedback && (
                <div className="mt-4 border-l-4 border-[#1E88E5] bg-[#1E88E5]/5 px-4 py-3 rounded-r-sm cv-markdown" data-testid={`cl-${section.id}-feedback`}>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">AI-feedback</div>
                  <ReactMarkdown components={MD_COMPONENTS}>{feedback}</ReactMarkdown>
                </div>
              )}
            </section>
          );
        })}

        {/* Section 5: generate full letter */}
        <section className="neuro-card-bold p-6 bg-white" data-testid="cl-section-final">
          <div className="flex items-start gap-3 mb-4">
            <div className="inline-flex h-9 w-9 items-center justify-center bg-[#43A047] text-white rounded-sm shrink-0">
              <span className="text-sm font-bold">5</span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <ChartLineUp size={20} weight="duotone" className="text-[#43A047]" />
                <h3 className="font-display text-lg font-bold tracking-tight">Je motivatiebrief</h3>
              </div>
              <p className="text-sm text-zinc-500 mt-1">
                De AI maakt op basis van je vier antwoorden hierboven een complete brief, met een sterke afsluiting
                die het bedrijf vraagt jou uit te nodigen voor een gesprek.
              </p>
              <div className="mt-3 border-l-4 border-[#FB8C00] bg-[#FB8C00]/5 px-3 py-2.5 rounded-r-sm text-sm text-[#E65100] flex items-start gap-2" data-testid="cl-letter-disclaimer">
                <Info size={16} weight="fill" className="mt-0.5 shrink-0" />
                <span>
                  <strong>Dit is alleen een voorbeeld.</strong> Het werkt beter als je in je eigen
                  woorden iets vergelijkbaars schrijft — dan komt de brief authentieker over.
                  Pas de tekst aan, zet je eigen stempel erop, en haal eruit wat bij jou past.
                </span>
              </div>
            </div>
          </div>

          {!allFilled && (
            <div className="border-l-4 border-[#FB8C00] bg-[#FB8C00]/5 px-3 py-2 rounded-r-sm text-sm text-[#E65100] flex items-start gap-2 mb-4" data-testid="cl-final-warning">
              <Info size={16} weight="fill" className="mt-0.5 shrink-0" />
              <span>Vul eerst alle vier de onderdelen hierboven in.</span>
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap mb-4">
            <button
              onClick={generate}
              disabled={!allFilled || generating}
              data-testid="cl-generate-button"
              className="inline-flex items-center gap-2 bg-[#43A047] text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#388E3C] disabled:opacity-50 transition-colors"
            >
              {generating ? <ArrowsClockwise size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
              {generating ? "Brief schrijven..." : letter.letter_text ? "Genereer opnieuw" : "Schrijf mijn brief"}
            </button>
            <span className="text-xs text-zinc-500">Duurt circa 15-30 seconden.</span>
          </div>

          {letter.letter_text ? (
            <div className="border border-zinc-300 rounded-sm bg-zinc-50/40">
              <textarea
                value={letter.letter_text}
                onChange={(e) => update({ letter_text: e.target.value })}
                rows={20}
                data-testid="cl-letter-text"
                className="w-full bg-transparent outline-none px-5 py-4 text-[14.5px] leading-relaxed font-serif whitespace-pre-wrap resize-y"
              />
            </div>
          ) : (
            <div className="border border-dashed border-zinc-300 rounded-sm px-5 py-12 text-center text-sm text-zinc-400" data-testid="cl-letter-empty">
              Hier verschijnt jouw brief zodra je op "Schrijf mijn brief" klikt.
            </div>
          )}
        </section>
      </div>
    </AppLayout>
  );
}
